import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Phone, Mail, MapPin, Clock, MessageCircle, 
  Send, CheckCircle, AlertCircle, Heart, 
  Calendar, Users, Award
} from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    urgency: 'normal'
  });
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitted(true);
  };

  const contactMethods = [
    {
      icon: Phone,
      title: '24/7 Helpline',
      description: 'Immediate support and guidance',
      contact: '1-888-968-1014',
      link: 'tel:1-888-968-1014',
      available: 'Available 24/7',
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600',
      borderColor: 'border-red-200'
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: 'General inquiries and information',
      contact: 'help@addictionrehabcall.com',
      link: 'mailto:help@addictionrehabcall.com',
      available: 'Response within 24 hours',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-200'
    },
    {
      icon: MessageCircle,
      title: 'Live Chat',
      description: 'Real-time assistance online',
      contact: 'Start Chat',
      link: '#',
      available: 'Mon-Fri, 8AM-8PM EST',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      borderColor: 'border-green-200'
    }
  ];

  const officeHours = [
    { day: 'Monday - Friday', hours: '8:00 AM - 8:00 PM EST' },
    { day: 'Saturday', hours: '9:00 AM - 6:00 PM EST' },
    { day: 'Sunday', hours: '10:00 AM - 4:00 PM EST' },
    { day: 'Crisis Helpline', hours: '24/7 - Always Available' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="text-center shadow-lg">
              <CardContent className="py-12">
                <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  Message Sent Successfully!
                </h1>
                <p className="text-gray-600 mb-8">
                  Thank you for reaching out. Our team will review your message and respond within 24 hours. 
                  If this is urgent, please call our 24/7 helpline.
                </p>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center gap-3">
                    <Phone className="h-5 w-5 text-red-600" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-red-800">Need Immediate Help?</p>
                      <a 
                        href="tel:1-888-968-1014" 
                        className="text-lg font-bold text-red-600 hover:underline"
                      >
                        1-888-968-1014
                      </a>
                    </div>
                  </div>
                </div>
                
                <Button onClick={() => setSubmitted(false)}>
                  Send Another Message
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-white/20 text-white border-0">
              We're Here to Help
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Contact Us for Support
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Reach out anytime for personalized assistance finding the right treatment center. 
              Our compassionate team is available 24/7 to guide you or your loved one toward recovery.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Contact Methods */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {contactMethods.map((method, index) => (
              <Card key={index} className={`border-2 ${method.borderColor} ${method.bgColor} hover:shadow-lg transition-shadow`}>
                <CardContent className="p-6 text-center">
                  <div className={`rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 ${method.bgColor}`}>
                    <method.icon className={`h-8 w-8 ${method.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{method.title}</h3>
                  <p className="text-gray-600 mb-4">{method.description}</p>
                  
                  <a 
                    href={method.link}
                    className={`text-lg font-bold ${method.iconColor} hover:underline block mb-2`}
                  >
                    {method.contact}
                  </a>
                  
                  <p className="text-sm text-gray-500">{method.available}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">
                  Send us a Message
                </CardTitle>
                <p className="text-gray-600">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        required
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        placeholder="How can we help?"
                      />
                    </div>
                    <div>
                      <Label htmlFor="urgency">Priority Level</Label>
                      <Select value={formData.urgency} onValueChange={(value) => handleInputChange('urgency', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low - General inquiry</SelectItem>
                          <SelectItem value="normal">Normal - Standard request</SelectItem>
                          <SelectItem value="high">High - Need help soon</SelectItem>
                          <SelectItem value="urgent">Urgent - Crisis situation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      required
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Please describe how we can help you..."
                      rows={5}
                    />
                  </div>

                  {formData.urgency === 'urgent' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-red-800 mb-1">
                            Crisis Situation Detected
                          </p>
                          <p className="text-sm text-red-600 mb-2">
                            For immediate assistance, please call our 24/7 crisis helpline instead of waiting for email response.
                          </p>
                          <Button asChild variant="outline" size="sm" className="border-red-300 text-red-700">
                            <a href="tel:1-888-968-1014">
                              <Phone className="h-4 w-4 mr-2" />
                              Call Now: 1-888-968-1014
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button type="submit" className="w-full" size="lg">
                    <Send className="h-5 w-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-6">
              {/* Office Hours */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900 flex items-center">
                    <Clock className="h-5 w-5 mr-2" />
                    Contact Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {officeHours.map((schedule, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <span className="font-medium text-gray-700">{schedule.day}</span>
                        <span className="text-gray-600">{schedule.hours}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Why Contact Us */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900">
                    Why Contact Us?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Free Consultation</p>
                        <p className="text-sm text-gray-600">Personalized guidance at no cost</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Expert Matching</p>
                        <p className="text-sm text-gray-600">Find the right treatment center for your needs</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Insurance Assistance</p>
                        <p className="text-sm text-gray-600">Help navigating coverage and costs</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Confidential Support</p>
                        <p className="text-sm text-gray-600">All conversations are private and secure</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Crisis Support */}
              <Card className="shadow-lg bg-red-50 border-red-200">
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="bg-red-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                      <Heart className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-red-800 mb-2">
                      In Crisis? We're Here 24/7
                    </h3>
                    <p className="text-red-600 mb-4 text-sm">
                      If you or someone you know is in immediate danger or having thoughts of self-harm, 
                      don't wait. Call our crisis helpline now.
                    </p>
                    <Button asChild className="bg-red-600 hover:bg-red-700 text-white">
                      <a href="tel:1-888-968-1014">
                        <Phone className="h-4 w-4 mr-2" />
                        Crisis Helpline: 1-888-968-1014
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
              Frequently Asked Questions
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How quickly will someone respond?</h3>
                <p className="text-gray-600 text-sm mb-6">
                  We typically respond to emails within 24 hours during business days. 
                  For urgent matters, please call our helpline for immediate assistance.
                </p>

                <h3 className="font-semibold text-gray-900 mb-2">Is my information confidential?</h3>
                <p className="text-gray-600 text-sm mb-6">
                  Yes, all communications are kept strictly confidential in accordance with 
                  HIPAA privacy regulations and our privacy policy.
                </p>

                <h3 className="font-semibold text-gray-900 mb-2">Do you charge for consultations?</h3>
                <p className="text-gray-600 text-sm">
                  No, all our consultation services are completely free. We're here to help 
                  you find treatment, not to charge you for guidance.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">What if I'm not ready for treatment?</h3>
                <p className="text-gray-600 text-sm mb-6">
                  That's okay. We provide information and support regardless of where you 
                  are in your recovery journey. No pressure, just guidance when you're ready.
                </p>

                <h3 className="font-semibold text-gray-900 mb-2">Can you help with insurance questions?</h3>
                <p className="text-gray-600 text-sm mb-6">
                  Yes, our team can help you understand your insurance benefits and find 
                  treatment centers that accept your specific coverage.
                </p>

                <h3 className="font-semibold text-gray-900 mb-2">Do you provide treatment directly?</h3>
                <p className="text-gray-600 text-sm">
                  No, we're a referral service that helps connect you with appropriate 
                  treatment centers and programs in your area.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;